import { orgSelectors } from 'ducks/orgs'
import { HardwareSelectorFilterSidebarContext } from 'elements/hardwareSelectorV2/elements/HardwareSelectorFilterSidebarContext'
import { omit } from 'lodash'
import { SellerIntegrationType } from 'opensolar-common'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import useHandleChangeSelectedDistributor from 'pages/ordering/subheader/useHandleChangeSelectedDistributor'
import type { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { Confirm } from 'ra-ui-materialui'
import { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import listQueryParamsFormatter from '../../input/listQueryParamsFormatter'
import BasicFilter from '../basic/BasicFilter'
import {
  DISTRIBUTOR_BRAND_FILTER_KEY_V2,
  EXHIBITOR_FILTER_KEY_V2,
  MANUFACTURER_FILTER_KEY_V2,
} from '../manufacturerV2/constants'
import {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
  OptionGenericType,
} from '../typeV2'
import { DISTRIBUTOR_FILTER_KEY_V2 } from './constants'

export const GLOBAL_SUPPLIER_NODE_KEY_V2 = 'global_distributors'
const defaultSelectedOptionValue = 'all'
export const SELECT_ALL_OPTION = { id: 'default', title: 'All', value: defaultSelectedOptionValue }

const REFERRER_FILTER_KEY = 'referrer'

export const getDistributorsOptions = (
  sellerIntegrations: SellerIntegrationType[],
  addAllOption?: boolean
): OptionGenericType<string>[] => {
  const options: OptionGenericType<string>[] = []
  if (addAllOption) {
    options.push(SELECT_ALL_OPTION)
  }

  // Map seller integrations to option generic type
  const sellerOptions = sellerIntegrations.map((integration) => ({
    id: integration.filterKey,
    title: integration.name,
    value: integration.filterKey,
  }))

  return [...options, ...sellerOptions]
}

const createGlobalDistributorFilterNode = <T,>({
  label = 'View by supplier',
  rendererComponent: RendererComponent,
  ...restConfigs
}: ConfigurableFilterOptionsType<string, string>): FilterComponentNodeV2 => {
  const FilterComponent = (filterProps: FilterGenericPropsType) => {
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
    const [desiredSupplier, setDesiredSupplier] = useState<HardwareSupplierFilterKeyType>()
    const handleSelectDistributor = useHandleChangeSelectedDistributor()
    const enabledSellerIntegrations = useSelector(orgSelectors.getEnabledSellerIntegrationsConfig)
    const distributorOptions = getDistributorsOptions(enabledSellerIntegrations, true)
    let defaultValue = listQueryParamsFormatter.parse<string>(new Set([SELECT_ALL_OPTION.value]))
    const { location } = useContext(HardwareSelectorFilterSidebarContext)
    const { getSupplierConfig } = useGetSupplierConfig()

    const selectedValue = filterProps.allFilterValues[DISTRIBUTOR_FILTER_KEY_V2]

    useEffect(() => {
      const filterValues = {
        ...filterProps.modifiableFilterValues,
        [REFERRER_FILTER_KEY]: location,
      }
      filterProps.setModifiableFilters(filterValues)
    }, [])

    if (distributorOptions.length === 0) {
      return null
    }

    const updateFilter = (newValue) => {
      if (selectedValue === newValue) return
      const newFilterValues = omit(filterProps.modifiableFilterValues, [
        EXHIBITOR_FILTER_KEY_V2,
        MANUFACTURER_FILTER_KEY_V2,
        DISTRIBUTOR_BRAND_FILTER_KEY_V2,
      ])

      newFilterValues[DISTRIBUTOR_FILTER_KEY_V2] = newValue

      filterProps.setModifiableFilters(newFilterValues)
    }

    const handleConfirm = () => {
      if (desiredSupplier) {
        const supplierFilterKey = getSupplierConfig(desiredSupplier)?.filterKey || ''
        supplierFilterKey && handleSelectDistributor(supplierFilterKey as HardwareSupplierFilterKeyType)
      }

      updateFilter(desiredSupplier)
      setShowConfirmation(false)
    }

    const sideEffect = <OptionValue, RendererValue>({ filterValues }: { filterValues: FilterValuesType }) => {
      const newFilterValue = { ...filterValues }
      const distributors = listQueryParamsFormatter.parse(filterValues[DISTRIBUTOR_FILTER_KEY_V2])

      if (distributors.has(SELECT_ALL_OPTION.value)) {
        const allDistributors = new Set([...distributorOptions.map((option) => option.value)])
        allDistributors.delete(SELECT_ALL_OPTION.value)

        if (location === 'design') {
          delete newFilterValue[DISTRIBUTOR_FILTER_KEY_V2]
        } else {
          newFilterValue[DISTRIBUTOR_FILTER_KEY_V2] = listQueryParamsFormatter.format({ value: allDistributors })
        }

        // It's one or the other when we send the query.
        delete newFilterValue[DISTRIBUTOR_BRAND_FILTER_KEY_V2]
      } else {
        // See above.
        delete newFilterValue[MANUFACTURER_FILTER_KEY_V2]
      }

      return newFilterValue
    }

    return (
      <>
        <BasicFilter
          label={label}
          rendererComponent={RendererComponent}
          defaultSelectedOptionValue={defaultValue}
          filterKey={DISTRIBUTOR_FILTER_KEY_V2}
          options={distributorOptions}
          sideEffect={sideEffect}
          {...restConfigs}
          {...filterProps}
        />
        <Confirm
          isOpen={showConfirmation}
          title={"Change this project's preferred distributor?"}
          content={
            "You can order hardware from leading distributors through OpenSolar's Design page. Changing your hardware distributor will show you stock availability and pricing from the selected distributor and will automatically update your project costs."
          }
          onClose={() => {
            setShowConfirmation(false)
          }}
          onConfirm={handleConfirm}
        />
      </>
    )
  }

  return {
    key: GLOBAL_SUPPLIER_NODE_KEY_V2,
    component: FilterComponent,
  }
}

export default createGlobalDistributorFilterNode
